import { useMemo, useState } from 'react';
import { enquiries as Api, type Enquiry, type EnquiryStatus, type EnquiryTopic } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';

const STATUSES: EnquiryStatus[] = ['open', 'replied', 'closed'];
const TOPICS: EnquiryTopic[] = ['General', 'Billing', 'Batch', 'Payment issue', 'Content'];

const when = (v: string) => {
  const d = new Date(String(v).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? v
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export function Enquiries() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [filter, setFilter] = useState<'all' | EnquiryStatus>('open');
  const [open, setOpen] = useState<Enquiry | null>(null);

  const rows = useMemo(() => {
    const all = data?.enquiries ?? [];
    return filter === 'all' ? all : all.filter((e) => e.status === filter);
  }, [data, filter]);

  const openCount = (data?.enquiries ?? []).filter((e) => e.status === 'open').length;

  const sel = useSelection(rows);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Enquiries</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : openCount ? `${openCount} waiting for a reply`
              : 'Nothing waiting for a reply.'}
            {data?.subscribers.length ? ` · ${data.subscribers.length} newsletter subscribers` : ''}
          </p>
        </div>
      </div>

      {error ? <div className="err">Could not load enquiries — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="sg" style={{ marginBottom: 16 }}>
          {(['all', ...STATUSES] as const).map((f) => (
            <span key={f} {...(filter === f ? { 'data-on': '' } : {})} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f[0]!.toUpperCase() + f.slice(1)}
            </span>
          ))}
        </div>

        <BulkActions
          path="/enquiries" noun="enquiry"
          rows={sel.picked.map((e) => ({ id: e.id, label: String(e.email) }))}
          actions={[['close', 'Close'], ['reopen', 'Reopen']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>From</th><th>Topic</th><th>Message</th><th>When</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={7} tick />
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="muted" style={{ padding: '26px 8px' }}>
                  {data?.enquiries.length ? 'Nothing with that status.' : 'No enquiries yet.'}
                </td></tr>
              ) : rows.map((e) => (
                <tr key={e.id} style={sel.isOn(e.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(e.id)} label={String(e.email)}
                    onChange={(v) => sel.toggle(e.id, v)} />
                  <td>
                    <div style={{ fontWeight: 700 }}>{e.name || '—'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>
                      {[e.email, e.mobile].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className="muted">{e.topic}</td>
                  <td style={{ maxWidth: 380 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.message}
                    </div>
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>{when(e.created_at)}</td>
                  <td>
                    <span className="tg" style={{
                      background: e.status === 'open' ? 'rgba(var(--goldrgb), .14)'
                        : e.status === 'replied' ? 'rgba(62,207,142,.14)' : 'rgba(var(--wash), calc(.06 * var(--washx)))',
                    }}>{e.status}</span>
                  </td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setOpen(e)}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open ? (
        <EnquiryView
          enquiry={open}
          onCancel={() => setOpen(null)}
          onSaved={async () => { setOpen(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function EnquiryView({ enquiry, onCancel, onSaved }: {
  enquiry: Enquiry;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [status, setStatus] = useState<EnquiryStatus>(enquiry.status);
  const [topic, setTopic] = useState<EnquiryTopic>(enquiry.topic);
  const [note, setNote] = useState(enquiry.reply_note);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  async function save() {
    setSaving('Saving…');
    try { await Api.update(enquiry.id, { status, topic, reply_note: note }); await onSaved(); }
    catch (e) { setSaving(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={`Enquiry · ${enquiry.name || enquiry.email}`}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{saving}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Close</button>
          <button className="b bg" type="button" onClick={() => void save()}>Save</button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <div className="crd" style={{ padding: '16px 18px', marginBottom: 16, background: 'rgba(var(--wash), calc(.02 * var(--washx)))' }}>
        <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginBottom: 8 }}>
          {when(enquiry.created_at)} · {[enquiry.email, enquiry.mobile].filter(Boolean).join(' · ')}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{enquiry.message}</div>
      </div>

      {/* Replying happens in whichever inbox the enquiry was mailed to — this
          records what was done, it does not send anything. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Topic">
          <select className="inp" value={topic} onChange={(e) => setTopic(e.target.value as EnquiryTopic)}>
            {TOPICS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className="inp" value={status} onChange={(e) => setStatus(e.target.value as EnquiryStatus)}>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      <Field label="What was done" hint="A note for whoever picks this up next. Nothing is emailed from here.">
        <textarea className="inp ta" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      <a className="b bo" href={`mailto:${enquiry.email}?subject=${encodeURIComponent('Re: your enquiry — Tradevix')}`}
        style={{ textDecoration: 'none' }}>Reply by email ↗</a>
    </Modal>
  );
}
